<template>
  <div class="booking-container">
    <v-layout row wrap>
      <v-flex xs12>
        <v-card flat class="booking-header elevation-2">
          <v-container fluid class="pa-4">
            <h2 class="booking-title earthwerk-font">PAYMENT INFORMATION</h2>
            <div class="title-decoration"></div>
          </v-container>
        </v-card>
      </v-flex>

      <v-flex xs12>
        <v-card flat class="booking-card elevation-2">
          <v-form @submit.prevent="postBooking()"
                  ref="form"
                  v-model="valid"
                  lazy-validation>
            <v-container fluid class="form-grid">

              <v-flex xs12 v-if="showMissingWarning && missingRequirements.length > 0" class="pa-2">
                <v-alert :value="true" type="warning" outline color="warning" class="missing-alert">
                  <div class="mb-1">
                    <strong>To complete your booking, please:</strong>
                  </div>
                  <ul class="missing-list">
                    <li v-for="(item, idx) in missingRequirements" :key="idx">
                      <v-icon small color="warning">radio_button_unchecked</v-icon>
                      <span v-html="item"></span>
                    </li>
                  </ul>
                </v-alert>
              </v-flex>

              <v-flex xs12 class="section-header">
                <v-icon color="primary" small>mdi-account-circle</v-icon>
                <span class="section-title">Personal Information</span>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.first_name"
                              label="First Name"
                              required
                              :rules="requiredRules"
                              :error="showMissingWarning && !booking.first_name"
                              error-count="1"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-account"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.last_name"
                              label="Last Name"
                              required
                              :rules="requiredRules"
                              :error="showMissingWarning && !booking.last_name"
                              error-count="1"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-account"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.email"
                              label="Email"
                              required
                              type="email"
                              :rules="emailRules"
                              :error="showMissingWarning && !isEmailValid(booking.email)"
                              error-count="1"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-email"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-autocomplete label="Country"
                                v-model="booking.code"
                                :items="allCountries"
                                :rules="requiredRules"
                                :error="showMissingWarning && !booking.code"
                                error-count="1"
                                item-text="name"
                                item-value="code"
                                @change="findCountryCode"
                                :loading="$loadingTrue"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-earth"></v-autocomplete>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.phone"
                              label="Phone * (country code locked)"
                              required
                              minlength="13"
                              :rules="phoneRules"
                              :error="showMissingWarning && !isPhoneValid(booking.phone)"
                              error-count="1"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-phone"
                              @keydown="guardPhonePrefix"></v-text-field>
              </v-flex>

              <v-flex md6 xs12 class="pa-2">
                <v-text-field v-model="booking.city"
                              label="City"
                              required
                              :rules="requiredRules"
                              :error="showMissingWarning && !booking.city"
                              error-count="1"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-city"></v-text-field>
              </v-flex>

              <v-flex xs12 class="pa-2">
                <v-text-field v-model="booking.promo_code"
                              label="Promo Code"
                              outlined
                              dense
                              class="modern-input"
                              prepend-inner-icon="mdi-ticket-percent"></v-text-field>
              </v-flex>

              <v-flex xs12 class="section-header clickable" @click="entrySectionExpanded = !entrySectionExpanded">
                <v-icon color="primary" small>mdi-map-marker</v-icon>
                <span class="section-title">Entry Information</span>
                <v-spacer></v-spacer>
                <span class="section-hint">{{ entrySectionExpanded ? 'Click to collapse' : 'Click to expand' }}</span>
                <v-icon color="primary" small class="section-toggle-icon">
                  {{ entrySectionExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
              </v-flex>

              <template v-if="entrySectionExpanded">
                <v-flex md6 xs12 class="pa-2">
                  <v-autocomplete label="Mode of Entry"
                                  v-model="booking.entry_mode"
                                  :items="allEntryModes"
                                  item-text="entry_mode_name"
                                  item-value="entry_mode_public_id"
                                  @change="findEntryPoints"
                                  :loading="$loadingTrue"
                                  outlined
                                  dense
                                  class="modern-input"
                                  prepend-inner-icon="mdi-vector-polyline"></v-autocomplete>
                </v-flex>

                <v-flex md6 xs12 class="pa-2">
                  <v-autocomplete label="Point of Entry"
                                  v-model="booking.entry_point"
                                  :items="allEntryPoints"
                                  item-text="gate_name"
                                  item-value="gate_public_id"
                                  :loading="$loadingTrue"
                                  outlined
                                  dense
                                  class="modern-input"
                                  prepend-inner-icon="mdi-gate"></v-autocomplete>
                </v-flex>

                <v-flex xs12 class="pa-2">
                  <v-text-field v-model="booking.mode_registration"
                                label="Mode registration Number"
                                outlined
                                dense
                                class="modern-input"
                                prepend-inner-icon="mdi-numeric"></v-text-field>
                </v-flex>

                <v-flex xs12 class="pa-2">
                  <v-textarea v-model="booking.additional_note"
                              label="Additional Note"
                              outlined
                              dense
                              class="modern-input"
                              rows="2"
                              prepend-inner-icon="mdi-note-text"></v-textarea>
                </v-flex>
              </template>

              <v-flex xs12 v-if="proofOfResidency.length > 0" class="residency-section pa-2">
                <div class="section-header clickable" @click="residency_proof = true">
                  <v-icon color="primary" small>mdi-file-document</v-icon>
                  <span class="section-title">Click Here To Proof Residency</span>
                </div>

                <v-dialog v-model="residency_proof" persistent max-width="800px">
                  <v-card class="proof-dialog elevation-4">
                    <v-card-title class="dialog-title">
                      Residency Proof Details
                      <v-spacer></v-spacer>
                      <v-btn icon @click="residency_proof = false">
                        <v-icon>mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text>
                      <div class="proof-grid">
                        <div v-for="(residency, key) in proofOfResidency" :key="key" class="proof-item">
                          <v-text-field readonly
                                        label="Residency"
                                        v-model="proofOfResidency[key].payment_person"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field readonly
                                        label="Point Of Proof"
                                        v-model="proofOfResidency[key].proof_point"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field label="First Name"
                                        v-model="proofOfResidency[key].first_name"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field label="Last Name"
                                        v-model="proofOfResidency[key].last_name"
                                        outlined
                                        dense></v-text-field>

                          <v-text-field label="Document Id"
                                        v-model="proofOfResidency[key].document_id"
                                        outlined
                                        dense></v-text-field>
                        </div>
                      </div>
                    </v-card-text>
                    <v-card-actions class="dialog-actions">
                      <v-btn color="primary"
                             depressed
                             @click="addProof()"
                             :loading="$loadingTrue"
                             class="action-btn">
                        <v-icon left small>mdi-check</v-icon>
                        Add Proof
                      </v-btn>
                      <v-btn color="secondary"
                             depressed
                             @click="residency_proof = false"
                             class="action-btn">
                        <v-icon left small>mdi-close</v-icon>
                        Cancel
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-flex>

              <v-flex xs12 class="terms-section pa-2">
                <v-alert v-if="showMissingWarning && (!booking.terms || !booking.disclaimer)"
                         :value="true"
                         type="warning"
                         outline
                         color="warning"
                         class="mb-3 terms-warning">
                  <strong>Please accept the required terms and liability disclaimer below.</strong>
                </v-alert>

                <div class="terms-item" :class="{ 'terms-item-error': showMissingWarning && !booking.terms }">
                  <router-link target="_blank"
                               :to="{ name: 'PTermsAndConditions' }"
                               class="terms-link">
                    <v-icon small>mdi-file-document</v-icon>
                    Ol Pejeta Conservancy terms and conditions
                  </router-link>
                  <v-checkbox v-model="booking.terms"
                              type="checkbox"
                              label="I agree to the terms and conditions"
                              color="primary"
                              hide-details
                              class="terms-checkbox"></v-checkbox>
                  <div v-if="showMissingWarning && !booking.terms" class="terms-error-msg">
                    <v-icon small color="warning">mdi-alert</v-icon>
                    You must agree to the terms and conditions
                  </div>
                </div>

                <div class="terms-item">
                  <a class="terms-link">
                    <v-icon small>mdi-email-newsletter</v-icon>
                    Receive Emails and Newsletters
                  </a>
                  <v-checkbox v-model="booking.newsletter"
                              type="checkbox"
                              label="I agree to receive promotional emails"
                              color="primary"
                              hide-details
                              class="terms-checkbox"></v-checkbox>
                </div>

                <div class="terms-item" :class="{ 'terms-item-error': showMissingWarning && !booking.disclaimer }">
                  <a class="terms-link" @click="openDisclaimer">
                    <v-icon small>mdi-alert</v-icon>
                    View Liability Disclaimer (Required)
                  </a>
                  <v-checkbox v-model="booking.disclaimer"
                              type="checkbox"
                              label="I accept the liability disclaimer"
                              color="primary"
                              hide-details
                              class="terms-checkbox"></v-checkbox>
                  <div v-if="showMissingWarning && !booking.disclaimer" class="terms-error-msg">
                    <v-icon small color="warning">mdi-alert</v-icon>
                    You must accept the liability disclaimer
                  </div>
                </div>

                <v-dialog v-model="showDisclaimer" persistent max-width="600px" content-class="disclaimer-dialog">
                  <v-card class="disclaimer-card elevation-4">
                    <v-card-title class="disclaimer-header">
                      <div class="disclaimer-header-content">
                        <v-icon left color="white" size="22">mdi-alert-circle</v-icon>
                        <span class="disclaimer-header-title">Liability Disclaimer</span>
                      </div>
                      <v-btn icon @click="showDisclaimer = false" class="disclaimer-close-btn">
                        <v-icon color="white" size="20">mdi-close</v-icon>
                      </v-btn>
                    </v-card-title>

                    <v-divider></v-divider>
                    <v-card-text class="disclaimer-body">
                      <div class="disclaimer-content">
                        <p class="disclaimer-intro">
                          <strong>Please read the following disclaimer carefully before proceeding.</strong>
                        </p>
                        <p>By entering the Ol Pejeta Conservancy ("the Conservancy") for any purpose, I acknowledge the inherent risks to my life and property.</p>
                        <p>I confirm that I have read and agree to abide by the Conservancy's "Code of Conduct" to ensure my safety to a reasonable extent.</p>
                        <p>I understand and accept that the Conservancy contains dangerous wild animals, including but not limited to lions, rhinos, elephants, buffaloes, snakes, and insects.</p>
                        <p><strong>By entering the Conservancy, I agree to the following:</strong></p>
                        <ul class="disclaimer-list">
                          <li>Waive and release the Conservancy, Ol Pejeta Conservancy Limited, and its employees, agents, affiliates, and officers from any liability.</li>
                          <li>Hold them harmless from any injury, claim, accident, loss, or damage, including personal injury (fatal or otherwise) and damage to personal property, resulting from my entry.</li>
                          <li>This waiver applies to myself and any minors accompanying me, and is binding on my assigns, heirs, and legal representatives.</li>
                        </ul>
                        <p>If any portion of this waiver is deemed unenforceable, that portion shall be struck out, but the remaining provisions will remain in full force and effect.</p>
                        <p class="disclaimer-footer">This waiver is governed by the laws of Kenya.</p>
                      </div>
                    </v-card-text>

                    <v-divider></v-divider>
                    <v-card-actions class="disclaimer-actions">
                      <v-btn color="grey"
                             text
                             @click="showDisclaimer = false"
                             class="disclaimer-close-action">
                        Close
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn color="primary"
                             depressed
                             @click="acceptDisclaimer"
                             class="disclaimer-agree-btn">
                        <v-icon left small>mdi-check</v-icon>
                        Agree
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-flex>

              <v-flex xs12 class="pa-2 submit-wrapper">
                <v-btn depressed
                       block
                       type="submit"
                       :disabled="processingBooking"
                       color="primary"
                       class="submit-btn"
                       large>
                  <span v-if="!processingBooking" class="submit-btn-inner">
                    <v-icon left small>mdi-check-circle</v-icon>
                    Submit Booking
                  </span>
                  <span v-else class="submit-btn-inner">
                    <v-progress-circular
                      indeterminate
                      size="20"
                      width="2"
                      color="white"
                      class="mr-2"></v-progress-circular>
                    Processing...
                  </span>
                </v-btn>
              </v-flex>
            </v-container>
          </v-form>
        </v-card>
      </v-flex>
    </v-layout>

    <v-dialog v-model="showAccountModal"
              persistent
              max-width="460px"
              :scrim="false"
              content-class="account-modal">
      <v-card class="elevation-4 account-card">
        <v-card-title class="account-modal-header">
          <div class="account-header-content">
            <v-icon color="white" size="26" class="mr-2">mdi-account-plus-outline</v-icon>
            <span class="earthwerk-font account-header-title">Create Your Guest Account</span>
          </div>
        </v-card-title>

        <v-card-text class="account-modal-body">
          <div class="account-intro">
            <v-icon color="primary" size="20" class="account-intro-icon">mdi-information-outline</v-icon>
            <p class="account-description">
              We'll set up a guest account so you can track your booking and access it anytime.
            </p>
          </div>

          <div class="account-info-block">
            <div class="account-info-row">
              <v-icon small color="#74a533">mdi-email-check-outline</v-icon>
              <span>
                A secure password will be sent to
                <strong>{{ accountForm.email || 'your email' }}</strong>
              </span>
            </div>
            <div class="account-info-row">
              <v-icon small color="#74a533">mdi-shield-check-outline</v-icon>
              <span>No spam — just your booking updates</span>
            </div>
            <div class="account-info-row">
              <v-icon small color="#74a533">mdi-lightning-bolt-outline</v-icon>
              <span>Faster check-out on future visits</span>
            </div>
          </div>

          <div class="account-email-preview">
            <v-text-field v-model="accountForm.email"
                          label="Account Email"
                          readonly
                          outlined
                          dense
                          prepend-inner-icon="mdi-email"
                          background-color="#f5f5f5"
                          class="account-input"></v-text-field>
          </div>

          <v-alert v-if="accountError"
                   type="error"
                   dense
                   class="mt-3"
                   dismissible
                   @input="accountError = ''">
            {{ accountError }}
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="account-modal-actions">
          <v-btn text
                 small
                 @click="skipAccountCreation"
                 :disabled="accountCreating"
                 class="skip-btn">
            Continue as Guest
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary"
                 depressed
                 small
                 @click="createAccount"
                 :loading="accountCreating"
                 :disabled="accountCreating"
                 class="create-btn">
            <v-icon left small>mdi-account-check-outline</v-icon>
            Create My Account
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "booking-information",
  data() {
    return {
      requiredRules: [(v) => (v !== undefined && v !== null && String(v).trim() !== "") || "This field is required"],
      emailRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],
      phoneRules: [
        (v) => !!v || "Phone is required",
        (v) => String(v).startsWith("+") || "Phone must start with + and country code",
        (v) => String(v).replace(/[^0-9]/g, "").length >= 11 || "Phone number is too short",
      ],
      valid: true,
      sheet: false,
      residency_proof: false,
      showDisclaimer: false,
      disclaimerViewed: false,
      showAccountModal: false,
      accountCreating: false,
      loading: false,
      accountError: '',
      processingBooking: false,
      showMissingWarning: false,
      currentBookingId: null,
      entrySectionExpanded: false,
      accountDecision: null,
      booking: {
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address: "",
        code: "",
        country: "",
        city: "",
        additional_note: "",
        promo_code: "",
        newsletter: false,
        country_details: {},
        entry_mode: "",
        entry_point: "",
        mode_registration: "",
        terms: false,
        disclaimer: false,
        type: "GB601X10",
      },
      accountForm: {
        email: "",
      },
      donation: {
        amount: "",
        currency: "",
        donation_to: "",
        cause: "",
        currency_name: "",
      },
      proof: {
        first_name: "",
        last_name: "",
        residency: "",
        document_id: "",
      },
    };
  },

  created() {
    this.$store.dispatch("get_page", "t&c");
    this.$store.dispatch("get_all_countries");
    this.$store.dispatch("get_full_conservation_fees");
    this.$store.dispatch("get_all_causes");
    this.$store.dispatch("get_all_gates");
    this.$store.dispatch("get_all_entry_modes");
    this.openDisclaimer();
  },

  computed: {
    allCountries() {
      return (this.$store.state.CountryStore && this.$store.state.CountryStore.all_countries) || [];
    },

    allEntryModes() {
      return (this.$store.state.EntryModeStore && this.$store.state.EntryModeStore.all_entry_modes) || [];
    },

    allEntryPoints: function () {
      let allEntry = this.allGates || [];
      let res = [];
      if (this.booking.entry_mode != '') {
        for (let i = 0; i < allEntry.length; i++) {
          if (allEntry[i].entry_mode_public_id == this.booking.entry_mode) {
            res.push(allEntry[i]);
          }
        }
      }
      return res;
    },

    allGates() {
      return (this.$store.state.GateStore && this.$store.state.GateStore.all_gates) || [];
    },

    missingRequirements() {
      const list = [];
      if (!this.booking.first_name) list.push("Enter your <strong>first name</strong>");
      if (!this.booking.last_name) list.push("Enter your <strong>last name</strong>");
      if (!this.isEmailValid(this.booking.email)) list.push("Enter a valid <strong>email address</strong>");
      if (!this.booking.code) list.push("Select your <strong>country</strong>");
      if (!this.isPhoneValid(this.booking.phone)) list.push("Enter a valid <strong>phone</strong> starting with + and country code");
      if (!this.booking.city) list.push("Enter your <strong>city</strong>");
      if (!this.booking.terms) list.push("Accept the <strong>terms and conditions</strong>");
      if (!this.booking.disclaimer) list.push("Accept the <strong>liability disclaimer</strong>");
      return list;
    },

    globalBookingDates() {
      if (this.$store.state.GlobalBookingStore && this.$store.state.GlobalBookingStore.booking_dates) {
        return this.$store.state.GlobalBookingStore.booking_dates;
      }
      return null;
    },

    hasGlobalBookingDates() {
      return this.globalBookingDates &&
        this.globalBookingDates.check_in &&
        this.globalBookingDates.check_in !== '' &&
        this.globalBookingDates.check_out &&
        this.globalBookingDates.check_out !== '';
    },

    bookingCheckIn() {
      if (this.hasGlobalBookingDates) {
        return this.globalBookingDates.check_in;
      }
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      return userData.check_in || '';
    },

    bookingCheckOut() {
      if (this.hasGlobalBookingDates) {
        return this.globalBookingDates.check_out;
      }
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      return userData.check_out || '';
    },

    bookingNights() {
      if (this.hasGlobalBookingDates && this.globalBookingDates.check_in && this.globalBookingDates.check_out) {
        const start = this.$moment(this.globalBookingDates.check_in);
        const end = this.$moment(this.globalBookingDates.check_out);
        return end.diff(start, 'days');
      }
      const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
      if (userData.check_in && userData.check_out) {
        const start = this.$moment(userData.check_in);
        const end = this.$moment(userData.check_out);
        return end.diff(start, 'days');
      }
      return 1;
    },

    chosenFacilities() {
      try { return JSON.parse(localStorage.getItem('facility_data') || '[]'); }
      catch (e) { return []; }
    },

    chosenInventory() {
      try { return JSON.parse(localStorage.getItem('activity_data') || '[]'); }
      catch (e) { return []; }
    },

    allConservationFees() {
      try { return JSON.parse(localStorage.getItem('conservation_data') || '[]'); }
      catch (e) { return []; }
    },

    allVehicles() {
      try { return JSON.parse(localStorage.getItem('vehicle_data') || '[]'); }
      catch (e) { return []; }
    },

    proofOfResidency() {
      try { return JSON.parse(localStorage.getItem('residency_proof_data') || '[]'); }
      catch (e) { return []; }
    },

    chosenExternalFacilities() {
      try { return JSON.parse(localStorage.getItem('external_facility_data') || '[]'); }
      catch (e) { return []; }
    }
  },

  watch: {
    'booking.phone'(val) {
      if (!this.booking.code) return;
      const country = this.allCountries.find(c => c.code === this.booking.code);
      if (!country || !country.calling_code) return;

      const prefix = '+' + country.calling_code;

      if (!val || !val.startsWith(prefix)) {
        this.booking.phone = prefix;
      }
    },
  },

  methods: {
    isEmailValid(v) {
      return !!v && /.+@.+\..+/.test(v);
    },

    isPhoneValid(v) {
      if (!v) return false;
      if (!String(v).startsWith("+")) return false;
      return String(v).replace(/[^0-9]/g, "").length >= 11;
    },

    guardPhonePrefix(e) {
      if (!this.booking.code) return;
      const country = this.allCountries.find(c => c.code === this.booking.code);
      if (!country || !country.calling_code) return;

      const prefix = '+' + country.calling_code;
      const input = e.target;
      const selStart = input.selectionStart;
      const selEnd = input.selectionEnd;

      if (e.key === 'Backspace' && selStart <= prefix.length && selStart === selEnd) {
        e.preventDefault();
      }
      if (e.key === 'Delete' && selStart < prefix.length) {
        e.preventDefault();
      }
    },

    findCountryCode(data) {
      var instance = this;
      this.allCountries.map(function (country) {
        if (data === country.code) {
          instance.booking.phone = "+" + country.calling_code;
          instance.booking.country_details = country;
        }
      });
    },

    addProof() {
      this.residency_proof = false;
    },

    postBooking: function () {
  if (this.processingBooking) return;

  this.showMissingWarning = true;

  const formValid = this.$refs.form.validate();
  const termsOk = this.booking.terms && this.booking.disclaimer;

  if (!formValid || !termsOk) {
    if (this.$toast) this.$toast.warning('Please complete the highlighted fields before submitting.');
    this.$nextTick(() => {
      const el = document.querySelector('.missing-alert');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return;
  }

  this.processingBooking = true;
  this.loading = true;
  this.accountCreating = false;
  this.accountError = '';
  this.accountForm.email = this.booking.email;
  this.accountDecision = null;
  this.showAccountModal = true;
},

    buildBookingPayload() {
      var self = this;

      var guests = (self.allConservationFees || []).map(function (g) {
        return {
          payment_public_id: g.payment_public_id,
          payment_person: g.payment_person,
          payment_guests: String(g.payment_guests || g.guest_count || 0),
          payment_schedule: g.payment_schedule,
          payment_person_discount: g.payment_person_discount || 0,
          reason: g.reason || null,
        };
      });

      var facilities = (self.chosenFacilities || []).map(function (f) {
        return Object.assign({}, f);
      });

      var inventory = (self.chosenInventory || []).map(function (i) {
        return Object.assign({}, i);
      });

      var vehicles = (self.allVehicles || []).map(function (v) {
        return {
          vehicle_charge_public_id: v.vehicle_charge_public_id,
          vehicles: String(v.vehicles || v.vehicle_count || 0),
          discount: v.discount || 0,
          reason: v.reason || null,
        };
      });

      var external_facilities = (self.chosenExternalFacilities || []).map(function (e) {
        return Object.assign({}, e);
      });

      var proof_of_residency = (self.proofOfResidency || []).map(function (p) {
        return Object.assign({}, p);
      });

      var combinedData = Object.assign(
        JSON.parse(localStorage.getItem("user_data") || "{}"),
        self.booking
      );

      if (self.hasGlobalBookingDates) {
        combinedData.check_in = self.bookingCheckIn;
        combinedData.check_out = self.bookingCheckOut;
        combinedData.days = self.bookingNights;
      }

      combinedData.type = combinedData.type || "GB601X10";
      combinedData.currency = self.$store.state.CurrencyStore.browser_currency_id;
      combinedData.currency_id = self.$store.state.CurrencyStore.browser_currency_id;
      combinedData.session_id =
        localStorage.getItem('session_token') ||
        localStorage.getItem('lkjhgfdsa') ||
        'guest';

      var data = Object.assign({}, combinedData, {
        guests: guests,
        facilities: facilities,
        inventory: inventory,
        vehicles: vehicles,
        external_facilities: external_facilities,
        proof_of_residency: proof_of_residency,
        donation: self.donation || {},
      });

      return data;
    },

    processBookingAfterDecision: function () {
      var self = this;

      if (!self.$store.state.CurrencyStore || !self.$store.state.CurrencyStore.browser_currency_id) {
        if (self.$toast) {
          self.$toast.error('Currency information is missing. Please refresh the page and try again.');
        }
        self.$store.dispatch("loading_false");
        self.loading = false;
        self.processingBooking = false;
        self.showAccountModal = false;
        return;
      }

      self.$store.dispatch("loading_true");
      self.loading = true;

      var data = self.buildBookingPayload();

      if (self.accountDecision === 'created') {
        self.createUserAccountThenBooking(data);
      } else {
        self.submitBookingOnly(data);
      }
    },

    createUserAccountThenBooking: function (bookingData) {
      var self = this;
      self.accountCreating = true;

      var userAccountData = {
        first_name: self.booking.first_name,
        last_name: self.booking.last_name,
        email: self.booking.email,
        phone_number: self.booking.phone,
        user_type: "acc1886a",
        has_account: true,
        session_id: bookingData.session_id || 'guest',
        customer_code_kes: '0000',
        customer_code_usd: '0000',
      };

      self.$store.dispatch("post_user_guest", userAccountData)
        .then(function (userResponse) {
          if (userResponse && userResponse.data) {
            if (userResponse.data.id) bookingData.user_id = userResponse.data.id;
            if (userResponse.data.public_id) bookingData.user_public_id = userResponse.data.public_id;
            if (userResponse.data.token || userResponse.data.access_token) {
              var token = userResponse.data.token || userResponse.data.access_token;
              localStorage.setItem('auth_token', token);
            }
          }
          return self.$store.dispatch("post_booking_public", bookingData);
        })
        .then(function (bookingResponse) {
          self.handleBookingSuccess(bookingResponse, true);
        })
        .catch(function (error) {
          self.accountCreating = false;

          var errorMsg = '';
          if (error.response && error.response.data) {
            errorMsg = error.response.data.message || error.response.data.error || '';
            if (typeof errorMsg === 'string' && errorMsg.includes('User already exists')) {
              if (self.$toast) self.$toast.warning('Account already exists. Proceeding with booking.');
              self.submitBookingOnly(bookingData);
              return;
            }
          } else if (error.message) {
            errorMsg = error.message;
            if (errorMsg.includes('already exists') || errorMsg.includes('User already')) {
              if (self.$toast) self.$toast.warning('Account already exists. Proceeding with booking.');
              self.submitBookingOnly(bookingData);
              return;
            }
          }

          self.handleBookingError(error);
        })
        .finally(function () {
          self.accountCreating = false;
        });
    },

    submitBookingOnly: function (bookingData) {
      var self = this;

      self.$store.dispatch("post_booking_public", bookingData)
        .then(function (bookingResponse) {
          self.handleBookingSuccess(bookingResponse, false);
        })
        .catch(function (error) {
          self.handleBookingError(error);
        });
    },

    handleBookingSuccess: function (response, accountCreated) {
      if (response && response.data) {
        var message = accountCreated
          ? 'Account created and booking submitted. Check your email for your password.'
          : 'Booking submitted successfully!';

        if (this.$toast) this.$toast.success(message);

        localStorage.setItem("previous_booking", JSON.stringify(response.data));

        if (accountCreated) {
          var accountInfo = {
            email: this.booking.email,
            created: true,
            booking_id: response.data.id || response.data.booking_id
          };
          localStorage.setItem('user_credentials', JSON.stringify(accountInfo));
        }

        var bookingId = response.data.id || response.data.booking_id;
        if (bookingId) {
          localStorage.setItem('current_booking_id', bookingId);
          this.currentBookingId = bookingId;
        }

        this.processingBooking = false;
        this.loading = false;
        this.$store.dispatch("loading_false");
        this.showAccountModal = false;

        this.$emit('payment-complete', response.data);
      } else {
        this.handleBookingError({ message: 'Booking response was empty.' });
      }
    },

    handleBookingError: function (error) {
  var errorMessage = 'Booking failed. Please try again.';

  if (error && error.response) {
    if (error.response.data) {
      if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      } else if (error.response.data.messages) {
        errorMessage = Array.isArray(error.response.data.messages)
          ? error.response.data.messages.join('; ')
          : String(error.response.data.messages);
      } else if (error.response.data.message) {
        errorMessage = Array.isArray(error.response.data.message)
          ? error.response.data.message.join('; ')
          : error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }

      if (error.response.data.errors) {
        var errors = error.response.data.errors;
        var errorList = [];
        Object.keys(errors).forEach(function (key) {
          if (Array.isArray(errors[key])) {
            errorList.push(key + ': ' + errors[key].join(', '));
          } else {
            errorList.push(key + ': ' + errors[key]);
          }
        });
        errorMessage = 'Validation errors: ' + errorList.join('; ');
      }
    }
  } else if (error && error.request) {
    errorMessage = 'No response from server. Please check your connection.';
  } else {
    errorMessage = (error && error.message) || 'Something went wrong. Please try again.';
  }

  this.processingBooking = false;
  this.loading = false;
  this.accountCreating = false;
  this.$store.dispatch("loading_false");

  if (this.showAccountModal) {
    this.accountError = errorMessage;
  } else {
    if (this.$toast) this.$toast.error(errorMessage);
  }
},

    createAccount: function () {
  if (this.accountCreating) return;

  if (!this.accountForm.email || !/.+@.+\..+/.test(this.accountForm.email)) {
    this.accountError = 'A valid email is required to create your account';
    if (this.$toast) this.$toast.error(this.accountError);
    return;
  }

  this.accountCreating = true;
  this.accountError = '';
  this.accountDecision = 'created';
  this.showAccountModal = false;

  if (this.$toast) this.$toast.success('Creating your account and booking...');

  this.processBookingAfterDecision();
},

skipAccountCreation: function () {
  if (this.accountCreating) return;

  this.accountDecision = 'skipped';
  this.accountError = '';
  this.showAccountModal = false;

  if (this.$toast) this.$toast.info('Continuing as guest. You can create an account later.');

  this.processBookingAfterDecision();
},

    findEntryPoints: function () {
      return this.allEntryPoints;
    },

    openDisclaimer() {
      this.showDisclaimer = true;
      this.disclaimerViewed = true;
    },

    acceptDisclaimer() {
      this.booking.disclaimer = true;
      this.showDisclaimer = false;
    },
  },
};
</script>

<style scoped>
@font-face {
  font-family: 'Earthwerk';
  src: url('PizzaDude.dk-Earthwerk.otf');
  font-weight: normal;
  font-style: normal;
}

.booking-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.booking-header {
  border-radius: 8px !important;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white;
}

.booking-title {
  color: white;
  font-size: 24px;
  letter-spacing: 1px;
  text-align: center;
  margin-bottom: 8px;
  text-shadow: 1px 1px 1px rgba(0,0,0,0.2);
}

.title-decoration {
  width: 60px;
  height: 4px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 2px;
  margin: 0 auto 8px;
}

.booking-card {
  border-radius: 8px !important;
  transition: all 0.3s ease;
  overflow: hidden;
  padding: 20px;
}

.booking-card:hover {
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}

.form-grid {
  display: flex;
  flex-wrap: wrap;
  margin: -8px;
}

.section-header {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;
  margin-bottom: 8px;
  border-bottom: 2px solid #eee;
}

.section-header.clickable {
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  user-select: none;
}

.section-header.clickable:hover {
  background-color: rgba(116, 165, 51, 0.08);
}

.section-title {
  font-weight: 600;
  color: #333;
  font-size: 16px;
}

.section-hint {
  font-size: 12px;
  color: #888;
  font-weight: 400;
  margin-right: 4px;
}

.section-toggle-icon {
  transition: transform 0.2s ease;
}

.modern-input {
  margin-bottom: 8px;
}

.residency-section {
  width: 100%;
}

.proof-dialog {
  border-radius: 8px !important;
}

.dialog-title {
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white;
  padding: 16px;
}

.proof-grid {
  padding: 16px 0;
}

.proof-item {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px;
  background: #f9f9f9;
  border-radius: 8px;
  margin-bottom: 16px;
}

.dialog-actions {
  padding: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  border-radius: 8px !important;
  min-width: 120px;
}

.terms-section {
  width: 100%;
  background: #f9f9f9;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
}

.terms-item {
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
}

.terms-item:last-child {
  margin-bottom: 0;
}

.terms-item.terms-item-error {
  background-color: rgba(255, 152, 0, 0.08);
  border-left: 4px solid #ff9800;
}

.terms-error-msg {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #e65100;
  font-size: 12.5px;
  font-weight: 600;
  margin-top: 4px;
  padding-left: 4px;
}

.terms-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #74a533;
  text-decoration: none;
  margin-bottom: 8px;
  font-size: 14px;
  cursor: pointer;
}

.terms-link:hover {
  text-decoration: underline;
}

.terms-checkbox {
  margin-top: 0;
}

.missing-alert {
  border-radius: 8px !important;
}

.missing-list {
  list-style: none;
  padding-left: 0;
  margin: 8px 0 0 0;
}

.missing-list li {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 0;
  font-size: 14px;
  color: #555;
}

.terms-warning {
  border-radius: 8px !important;
}

.submit-wrapper {
  margin-top: 8px;
}

.submit-btn {
  border-radius: 8px !important;
  margin-top: 8px;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%) !important;
  color: white !important;
  font-size: 16px;
  padding: 12px 24px;
  height: auto !important;
  min-height: 56px;
}

.submit-btn .v-icon {
  font-size: 20px !important;
}

.submit-btn-inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff !important;
  font-weight: 600;
}

.submit-btn.v-btn--disabled {
  opacity: 1 !important;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%) !important;
}

.submit-btn.v-btn--disabled .submit-btn-inner {
  color: #ffffff !important;
}

.submit-btn .v-progress-circular {
  color: #ffffff !important;
}

.submit-btn .v-progress-circular svg circle {
  stroke: #ffffff !important;
}

.earthwerk-font {
  font-family: 'Earthwerk', sans-serif;
  color: white;
  line-height: 1.3;
  letter-spacing: 0.5px;
}

.disclaimer-dialog {
  margin: 16px !important;
  max-width: 600px !important;
}

.disclaimer-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.disclaimer-header {
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
}

.disclaimer-header-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.disclaimer-header-title {
  font-family: 'Earthwerk', sans-serif;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.disclaimer-close-btn {
  margin: -8px;
}

.disclaimer-body {
  padding: 12px 16px !important;
  max-height: 60vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.disclaimer-content {
  font-size: 12px;
  line-height: 1.5;
  color: #333;
}

.disclaimer-content p {
  margin-bottom: 6px;
  font-size: 12px;
}

.disclaimer-intro {
  color: #4a6b22;
  font-size: 13px !important;
}

.disclaimer-list {
  padding-left: 18px;
  margin: 6px 0;
}

.disclaimer-list li {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.disclaimer-footer {
  font-style: italic;
  color: #666;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid #eee;
  font-size: 11px;
}

.disclaimer-actions {
  padding: 10px 16px !important;
  flex-wrap: wrap;
  gap: 6px;
  background: #fafafa;
}

.disclaimer-close-action {
  font-size: 12px !important;
  text-transform: none !important;
  padding: 4px 12px !important;
}

.disclaimer-agree-btn {
  border-radius: 8px !important;
  font-size: 12px !important;
  text-transform: none !important;
  padding: 4px 16px !important;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%) !important;
  color: white !important;
  min-width: 80px;
}

.account-modal {
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.account-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.account-modal-header {
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%);
  color: white;
  padding: 14px 20px;
  display: flex;
  align-items: center;
}

.account-header-content {
  display: flex;
  align-items: center;
}

.account-header-title {
  font-size: 16px;
  color: white;
  letter-spacing: 0.4px;
}

.account-modal-body {
  padding: 20px 22px 8px 22px;
}

.account-intro {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #f4f8ee;
  border-left: 4px solid #74a533;
  border-radius: 6px;
  margin-bottom: 16px;
}

.account-intro-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.account-description {
  color: #444;
  line-height: 1.5;
  font-size: 13.5px;
  margin: 0;
}

.account-info-block {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 18px;
}

.account-info-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #555;
  padding: 6px 0;
  line-height: 1.4;
}

.account-info-row:not(:last-child) {
  border-bottom: 1px dashed #e8e8e8;
}

.account-info-row strong {
  color: #2e5c14;
  font-weight: 600;
  word-break: break-all;
}

.account-email-preview {
  margin-bottom: 4px;
}

.account-input {
  margin-bottom: 0 !important;
}

.account-modal-actions {
  padding: 12px 20px 16px 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.skip-btn {
  color: #666 !important;
  font-size: 13px;
  text-transform: none !important;
  font-weight: 600 !important;
  padding: 6px 16px !important;
  background: rgba(0, 0, 0, 0.05) !important;
  border-radius: 8px !important;
  border: 1px solid #ddd !important;
}

.skip-btn:hover {
  color: #333 !important;
  background: rgba(0, 0, 0, 0.1) !important;
  border-color: #aaa !important;
}

.create-btn {
  border-radius: 8px !important;
  font-size: 13px;
  text-transform: none !important;
  padding: 6px 18px !important;
  min-width: 140px;
  background: linear-gradient(135deg, #74a533 0%, #587e27 100%) !important;
  color: white !important;
  font-weight: 600;
}

.create-btn .v-icon {
  font-size: 16px !important;
}

@media (max-width: 960px) {
  .booking-title {
    font-size: 20px;
  }

  .account-modal {
    max-width: 95% !important;
  }

  .disclaimer-dialog {
    margin: 12px !important;
  }

  .disclaimer-body {
    max-height: 55vh;
  }

  .disclaimer-content,
  .disclaimer-content p,
  .disclaimer-list li {
    font-size: 12px;
  }

  .submit-btn {
    font-size: 14px;
    padding: 10px 20px;
    min-height: 48px;
  }
}

@media (max-width: 600px) {
  .booking-container {
    padding: 10px;
  }

  .booking-card {
    padding: 12px;
    border-radius: 6px !important;
  }

  .booking-title {
    font-size: 17px;
  }

  .title-decoration {
    width: 40px;
    height: 3px;
  }

  .section-header {
    padding: 8px 4px;
  }

  .section-title {
    font-size: 14px;
  }

  .section-hint {
    font-size: 10px;
  }

  .section-header .v-icon {
    font-size: 18px !important;
  }

  .modern-input {
    margin-bottom: 4px;
  }

  .modern-input .v-label {
    font-size: 12px !important;
  }

  .proof-item {
    grid-template-columns: 1fr;
    padding: 12px;
  }

  .dialog-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    min-width: auto;
  }

  .terms-section {
    padding: 12px;
    margin: 10px 0;
  }

  .terms-link {
    font-size: 12px;
  }

  .terms-checkbox .v-label {
    font-size: 12px !important;
  }

  .missing-list li {
    font-size: 12px;
  }

  .submit-wrapper {
    padding: 4px 8px !important;
  }

  .submit-btn {
    font-size: 13px;
    padding: 10px 16px;
    min-height: 44px;
  }

  .submit-btn .v-icon {
    font-size: 16px !important;
  }

  .disclaimer-dialog {
    margin: 8px !important;
  }

  .disclaimer-card {
    border-radius: 10px !important;
  }

  .disclaimer-header {
    padding: 8px 12px;
    min-height: 40px;
  }

  .disclaimer-header-title {
    font-size: 12px;
  }

  .disclaimer-body {
    padding: 10px 12px !important;
    max-height: 50vh;
  }

  .disclaimer-content {
    font-size: 11px;
    line-height: 1.4;
  }

  .disclaimer-content p {
    font-size: 11px;
    margin-bottom: 4px;
  }

  .disclaimer-intro {
    font-size: 12px !important;
  }

  .disclaimer-list {
    padding-left: 14px;
  }

  .disclaimer-list li {
    font-size: 11px;
    margin-bottom: 3px;
  }

  .disclaimer-footer {
    font-size: 10px;
  }

  .disclaimer-actions {
    padding: 8px 12px !important;
    flex-direction: column;
  }

  .disclaimer-actions .v-spacer {
    display: none;
  }

  .disclaimer-close-action {
    font-size: 11px !important;
    padding: 6px 12px !important;
    width: 100%;
    order: 2;
  }

  .disclaimer-agree-btn {
    font-size: 11px !important;
    padding: 8px 16px !important;
    width: 100%;
    order: 1;
    min-width: auto;
  }

  .account-modal-body {
    padding: 16px 16px 4px 16px !important;
  }

  .account-description {
    font-size: 12.5px;
  }

  .account-info-row {
    font-size: 12px;
  }

  .account-header-title {
    font-size: 14px;
  }

  .account-modal-actions {
    padding: 10px 14px 14px 14px !important;
    flex-direction: column;
  }

  .account-modal-actions .v-spacer {
    display: none;
  }

  .create-btn {
    font-size: 12px !important;
    padding: 6px 14px !important;
    width: 100%;
    min-width: auto;
    order: 1;
  }

  .skip-btn {
    font-size: 12px !important;
    width: 100%;
    order: 2;
  }
}

@media (max-width: 360px) {
  .booking-container {
    padding: 6px;
  }

  .booking-card {
    padding: 8px;
  }

  .booking-title {
    font-size: 15px;
  }

  .section-title {
    font-size: 12px;
  }

  .section-hint {
    font-size: 9px;
  }

  .section-header .v-icon {
    font-size: 16px !important;
  }

  .modern-input .v-label {
    font-size: 11px !important;
  }

  .submit-btn {
    font-size: 12px;
    padding: 8px 12px;
    min-height: 40px;
  }

  .disclaimer-content,
  .disclaimer-content p,
  .disclaimer-list li {
    font-size: 10px;
  }

  .disclaimer-header-title {
    font-size: 11px;
  }

  .account-header-title {
    font-size: 13px;
  }

  .account-description {
    font-size: 11.5px;
  }

  .account-info-row {
    font-size: 11px;
  }

  .create-btn {
    font-size: 11px !important;
    padding: 6px 10px !important;
  }

  .skip-btn {
    font-size: 11px !important;
  }
}

@media (max-height: 500px) and (orientation: landscape) {
  .disclaimer-body {
    max-height: 35vh;
  }

  .disclaimer-header {
    padding: 6px 12px;
    min-height: 36px;
  }

  .disclaimer-header-title {
    font-size: 12px;
  }

  .disclaimer-content,
  .disclaimer-content p {
    font-size: 10px;
  }

  .disclaimer-content p {
    margin-bottom: 3px;
  }

  .disclaimer-list li {
    font-size: 10px;
    margin-bottom: 2px;
  }

  .disclaimer-actions {
    padding: 4px 10px !important;
  }

  .disclaimer-agree-btn {
    font-size: 10px !important;
    padding: 4px 12px !important;
  }

  .disclaimer-close-action {
    font-size: 10px !important;
    padding: 4px 10px !important;
  }
}

@media (min-width: 1200px) {
  .booking-container {
    padding: 30px 40px;
  }

  .booking-title {
    font-size: 28px;
  }

  .booking-card {
    padding: 30px;
  }

  .submit-btn {
    font-size: 18px;
    padding: 14px 32px;
    min-height: 64px;
  }
}

@media (min-width: 1600px) {
  .booking-container {
    max-width: 1400px;
    padding: 40px 60px;
  }

  .booking-title {
    font-size: 32px;
  }
}

.text-success {
  color: #4caf50 !important;
}

.text-caption {
  font-size: 12px !important;
}
</style>